import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Container, Table, Button, Modal, Form, Badge } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import axios from 'axios'
import './Admin.css'
import V2Layout from '../components/V2Layout'

const API = ''

function formatDate(d) {
    if (!d) return ''
    return new Date(d + 'Z').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const NewsAdminScreen = () => {
    const [articles, setArticles] = useState([])
    const [showModal, setShowModal] = useState(false)
    const [editArticle, setEditArticle] = useState(null)
    const [form, setForm] = useState({ title: '', body: '', header_image: '', status: 'draft' })
    const [showDelete, setShowDelete] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [uploadingHeader, setUploadingHeader] = useState(false)
    const quillRef = useRef(null)

    const load = useCallback(() => {
        axios.get(`${API}/api/news?all=1`).then(r => setArticles(r.data)).catch(() => { })
    }, [])

    useEffect(() => { load() }, [load])

    const openAdd = () => {
        setEditArticle(null)
        setForm({ title: '', body: '', header_image: '', status: 'draft' })
        setShowModal(true)
    }

    const openEdit = (a) => {
        setEditArticle(a)
        setForm({ title: a.title || '', body: a.body || '', header_image: a.header_image || '', status: a.status || 'draft' })
        setShowModal(true)
    }

    const save = async () => {
        if (editArticle) await axios.put(`${API}/api/news/${editArticle.id}`, form)
        else await axios.post(`${API}/api/news`, form)
        setShowModal(false)
        load()
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        await axios.delete(`${API}/api/news/${deleteTarget.id}`)
        setShowDelete(false)
        setDeleteTarget(null)
        load()
    }

    // Header image upload
    const handleHeaderUpload = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        setUploadingHeader(true)
        const fd = new FormData()
        fd.append('image', file)
        try {
            const res = await axios.post(`${API}/api/news-images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            setForm(prev => ({ ...prev, header_image: res.data.url }))
        } catch (err) { console.error(err) }
        setUploadingHeader(false)
    }

    // Quill inline image handler
    const imageHandler = useCallback(() => {
        const input = document.createElement('input')
        input.setAttribute('type', 'file')
        input.setAttribute('accept', 'image/*')
        input.click()
        input.onchange = async () => {
            const file = input.files[0]
            if (!file) return
            const fd = new FormData()
            fd.append('image', file)
            try {
                const res = await axios.post(`${API}/api/news-images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                const editor = quillRef.current?.getEditor()
                if (editor) {
                    const range = editor.getSelection(true)
                    editor.insertEmbed(range.index, 'image', `${API}${res.data.url}`)
                    editor.setSelection(range.index + 1)
                }
            } catch (err) { console.error(err) }
        }
    }, [])

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ align: [] }],
                ['blockquote', 'code-block'],
                ['link', 'image'],
                ['clean']
            ],
            handlers: { image: imageHandler }
        }
    }), [imageHandler])

    return (
        <V2Layout pageClass="pfc-admin">
            <style>{`
                .pfc-admin-container {
                    padding: 3rem 0 5rem;
                    background-color: #faf9f6;
                    min-height: 80vh;
                }
                .pfc-admin-card {
                    background: #fff;
                    border: 1px solid #e5e3dd;
                    border-radius: 8px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
                    padding: 32px;
                    margin-bottom: 24px;
                }
                .pfc-admin-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    border: 1px solid #e5e3dd;
                    border-radius: 6px;
                    overflow: hidden;
                    background-color: #fff;
                    margin-top: 1.5rem;
                }
                .pfc-admin-table thead th {
                    background-color: #26215C !important;
                    color: #fff !important;
                    font-family: var(--pfc-mono, monospace);
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    font-weight: 600;
                    border: none;
                    padding: 12px 16px;
                }
                .pfc-admin-table tbody td {
                    padding: 12px 16px;
                    vertical-align: middle;
                    font-size: 14px;
                    color: #333;
                    border-bottom: 1px solid #f1efe8;
                }
                .pfc-admin-table tbody tr:last-child td {
                    border-bottom: none;
                }
                .pfc-admin-table tbody tr {
                    transition: background-color 0.15s ease;
                }
                .pfc-admin-table tbody tr:hover {
                    background-color: rgba(38, 33, 148, 0.01) !important;
                }
                .btn-pfc-primary {
                    background-color: #26215C !important;
                    border-color: #26215C !important;
                    color: #fff !important;
                    font-family: var(--pfc-mono, monospace);
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    padding: 8px 16px;
                    border-radius: 4px;
                    font-weight: 600;
                    transition: all 0.2s ease;
                }
                .btn-pfc-primary:hover, .btn-pfc-primary:focus {
                    background-color: #1a1645 !important;
                    border-color: #1a1645 !important;
                    color: #fff !important;
                    box-shadow: 0 4px 12px rgba(38, 33, 92, 0.15);
                }
                .btn-pfc-secondary {
                    background-color: #fff !important;
                    border-color: #ced4da !important;
                    color: #5c5a54 !important;
                    font-family: var(--pfc-mono, monospace);
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    padding: 8px 16px;
                    border-radius: 4px;
                    font-weight: 600;
                    transition: all 0.2s ease;
                }
                .btn-pfc-secondary:hover {
                    background-color: #faf9f6 !important;
                    border-color: #adb5bd !important;
                    color: #333 !important;
                }
            `}</style>

            <section className="pfc-admin-container">
                <Container>
                    <div className="pfc-admin-card">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                            <div>
                                <h1 style={{ color: '#26215C', fontSize: '2rem', fontWeight: 700, margin: 0 }}>News Admin</h1>
                                <p className="text-muted mb-0 mt-1" style={{ fontSize: '13px' }}>
                                    Publish press releases, edit rich-text blog posts, manage status, and upload header images.
                                </p>
                            </div>
                            <Button className="btn-pfc-primary" onClick={openAdd}>+ New Article</Button>
                        </div>

                        {/* Top sub-navigation bar */}
                        <div className="d-flex gap-3 mb-4 align-items-center pfc-admin-subnav">
                            <Link to="/admin" className="pfc-admin-subnav-link">Dashboard</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/products/admin" className="pfc-admin-subnav-link">Products Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/capabilities/admin" className="pfc-admin-subnav-link">Capabilities Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/news/admin" className="pfc-admin-subnav-link active">News Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/story/admin" className="pfc-admin-subnav-link">Timeline Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/certifications/admin" className="pfc-admin-subnav-link">Certifications Admin</Link>
                        </div>

                        <Table bordered hover responsive className="pfc-admin-table">
                            <thead><tr><th>Title</th><th style={{ width: '100px' }}>Status</th><th style={{ width: '120px' }}>Date</th><th style={{ width: '140px' }}>Actions</th></tr></thead>
                            <tbody>
                                {articles.map(a => (
                                    <tr key={a.id}>
                                        <td>{a.title}</td>
                                        <td><Badge bg={a.status === 'published' ? 'success' : 'warning'}>{a.status}</Badge></td>
                                        <td style={{ fontSize: '0.85rem' }}>{formatDate(a.created_at)}</td>
                                        <td>
                                            <Button size="sm" variant="outline-primary" onClick={() => openEdit(a)} style={{ marginRight: '0.5rem' }}>Edit</Button>
                                            <Button size="sm" variant="outline-danger" onClick={() => { setDeleteTarget(a); setShowDelete(true) }}>Del</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {/* ── Article Modal ── */}
                    <Modal show={showModal} onHide={() => setShowModal(false)} centered size="xl" scrollable>
                        <Modal.Header closeButton>
                            <Modal.Title>{editArticle ? 'Edit Article' : 'New Article'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontWeight: 600 }}>Title</Form.Label>
                                <Form.Control value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Article title" />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontWeight: 600 }}>Status</Form.Label>
                                <Form.Select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontWeight: 600 }}>Header Image</Form.Label>
                                {form.header_image && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <img src={`${API}${form.header_image}`} alt="Header" style={{ maxHeight: '150px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                        <button onClick={() => setForm({ ...form, header_image: '' })} style={{ display: 'block', background: 'none', border: 'none', color: '#dc3545', fontSize: '0.8rem', cursor: 'pointer', marginTop: '0.25rem' }}>Remove header image</button>
                                    </div>
                                )}
                                <input type="file" accept="image/*" onChange={handleHeaderUpload} style={{ fontSize: '0.85rem' }} />
                                {uploadingHeader && <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '0.5rem' }}>Uploading...</span>}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontWeight: 600 }}>Body</Form.Label>
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={form.body}
                                    onChange={val => setForm(prev => ({ ...prev, body: val }))}
                                    modules={quillModules}
                                    style={{ minHeight: '300px', backgroundColor: '#fff' }}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button className="btn-pfc-secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                            <Button className="btn-pfc-primary" onClick={save}>Save</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* ── Delete Confirmation ── */}
                    <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title style={{ color: '#dc3545' }}>Confirm Delete</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Are you sure you want to delete <strong>{deleteTarget?.title}</strong>?</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button className="btn-pfc-secondary" onClick={() => setShowDelete(false)}>Cancel</Button>
                            <Button variant="danger" style={{ fontFamily: 'var(--pfc-mono, monospace)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 16px' }} onClick={confirmDelete}>Delete</Button>
                        </Modal.Footer>
                    </Modal>
                </Container>
            </section>
        </V2Layout>
    )
}

export default NewsAdminScreen
